import { useNavigate } from '@tanstack/react-router'
import { useDrinkHistory } from '@/features/drink-history/api/use-drink-history'
import { formatRelativeDate, formatTime } from '@/shared/lib/format'
import { useTelegram } from '@/shared/lib/telegram'
import { RotateCcw, Star, Coffee } from 'lucide-react'
import { OptimizedImage } from '@/shared/ui'

/**
 * Виджет: Недавно пили
 * 
 * Горизонтальный скролл карточек последних записей чайного дневника.
 * Позволяет просмотреть детали записи или быстро повторить чай.
 */
export function HomeRecent() {
  const navigate = useNavigate()
  const { hapticFeedback } = useTelegram()
  const { data, isLoading } = useDrinkHistory({ limit: 10 })
  
  const entries = data?.data || []
  
  const handleCardClick = () => {
    hapticFeedback.impactOccurred('light')
    navigate({ to: '/tea-diary' })
  }
  
  const handleRepeatClick = (e: React.MouseEvent, entry: typeof entries[0]) => {
    e.stopPropagation()
    hapticFeedback.impactOccurred('medium')
    
    // Переходим на страницу добавления с ID чая для клонирования
    navigate({
      to: '/tea-diary/add',
      search: {
        teaId: entry.teaId || undefined,
      },
    })
  }
  
  if (isLoading) {
    return (
      <div className="mb-6">
        <div className="px-4 mb-4">
          <h2 className="text-xl font-bold text-ink">Недавно вы пили</h2>
        </div>
        <div className="flex gap-4 px-4 overflow-x-auto pb-2">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="flex-shrink-0 w-48 h-32 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 dark:from-dark-elevated dark:via-dark-card dark:to-dark-elevated rounded-xl animate-pulse"
            />
          ))}
        </div>
      </div>
    )
  }
  
  if (entries.length === 0) {
    return (
      <div className="mb-5 px-4">
        <h2 className="text-xl font-bold text-ink dark:text-white mb-3">Ваши недавние впечатления</h2>
        <div className="bg-gray-50 dark:bg-dark-card rounded-xl p-6 text-center border border-silk dark:border-dark-border shadow-sm dark:shadow-md">
          {/* Минималистичная иллюстрация чашки */}
          <div className="mb-4 flex justify-center">
            <div className="relative w-16 h-16">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary-dark/15 dark:to-primary-dark/10" />
              <div className="absolute inset-2 rounded-full border-2 border-primary/20 dark:border-primary-dark/30" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 border-2 border-primary/30 dark:border-primary-dark/40 rounded-full" />
            </div>
          </div>
          <p className="text-gray-600 dark:text-gray-300 mb-1.5 text-sm font-medium">
            Здесь появятся чаи, которые вы пробовали недавно
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Записывайте впечатления, чтобы ничего не забывать.
          </p>
        </div>
      </div>
    )
  }
  
  return (
    <div className="widget-card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="section-title text-lg sm:text-xl">Ваши недавние впечатления</h2>
        <button
          onClick={() => {
            hapticFeedback.impactOccurred('light')
            navigate({ to: '/tea-diary' })
          }}
          className="text-sm text-primary dark:text-primary-light font-medium touch-target hover:underline"
        >
          Смотреть всё
        </button>
      </div>
      
      {/* На mobile - горизонтальный скролл, на desktop - сетка */}
      <div className="flex gap-4 overflow-x-auto lg:grid lg:grid-cols-2 xl:grid-cols-3 lg:overflow-visible pb-2 lg:pb-0 scrollbar-hide -mx-4 px-4 sm:-mx-6 sm:px-6 lg:mx-0 lg:px-0">
        {entries.slice(0, 10).map((entry) => {
          const entryDate = new Date(entry.drankAt)
          const isToday = entryDate.toDateString() === new Date().toDateString()
          const isYesterday =
            entryDate.toDateString() ===
            new Date(Date.now() - 86400000).toDateString()
          
          return (
            <div
              key={entry.id}
              onClick={handleCardClick}
              className="flex-shrink-0 w-44 lg:w-auto bg-white dark:bg-dark-card rounded-2xl border border-gray-100 dark:border-dark-border shadow-sm dark:shadow-md cursor-pointer hover:shadow-lg dark:hover:shadow-xl active:scale-[0.98] transition-all overflow-hidden"
            >
              {/* Изображение или плейсхолдер */}
              <div className="relative h-24 bg-gray-100 dark:bg-dark-elevated">
                {(entry.userPhotoUrl || entry.photoUrl) ? (
                  <OptimizedImage
                    src={entry.userPhotoUrl || entry.photoUrl || ''}
                    alt={entry.teaName || 'Чай'}
                    preset="card"
                    wrapperClassName="h-full w-full"
                    className="object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/5 to-primary/10 dark:from-primary/10 dark:to-primary/20">
                    <Coffee className="w-8 h-8 text-primary/30 dark:text-primary-light/30" />
                  </div>
                )}
                
                {/* Рейтинг поверх изображения */}
                {entry.rating && (
                  <div className="absolute top-2 right-2 flex items-center gap-0.5 bg-black/50 backdrop-blur-sm rounded-full px-2 py-0.5">
                    {[1, 2, 3, 4, 5].map((value) => (
                      <Star
                        key={value}
                        className={`h-3 w-3 ${
                          value <= entry.rating!
                            ? 'fill-amber-400 text-amber-400'
                            : 'fill-white/30 text-white/30'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
              
              {/* Контент */}
              <div className="p-3">
                <h3 className="font-semibold text-ink dark:text-white line-clamp-1 text-sm">
                  {entry.teaName || 'Чай'}
                </h3>
                
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {isToday ? (
                    <span>Сегодня • {formatTime(entryDate)}</span>
                  ) : isYesterday ? (
                    <span>Вчера • {formatTime(entryDate)}</span>
                  ) : (
                    <span>{formatRelativeDate(entryDate)}</span>
                  )}
                </div>
                
                <button
                  onClick={(e) => handleRepeatClick(e, entry)}
                  className="flex items-center gap-1.5 text-primary dark:text-primary-light text-xs font-medium mt-2 touch-target"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Повторить
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
